import { AlertTriangle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { AgeBand } from "../../shared/types";
import { AnswerGrid } from "../components/test/AnswerGrid";
import { ProgressHeader } from "../components/test/ProgressHeader";
import { QuestionCard } from "../components/test/QuestionCard";
import { TestCompletionScreen } from "../components/test/TestCompletionScreen";
import { TestIntroScreen } from "../components/test/TestIntroScreen";
import { TestShell } from "../components/test/TestShell";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useTestAttempt } from "../hooks/useTestAttempt";

const ageOptions: Array<{ value: AgeBand; label: string }> = [
  { value: "18-24", label: "18–24" },
  { value: "25-34", label: "25–34" },
  { value: "35-44", label: "35–44" },
  { value: "45-54", label: "45–54" },
  { value: "55-plus", label: "55+" }
];

function AgeRangeStep({ selected, onSelect, onContinue }: { selected: AgeBand | null; onSelect: (value: AgeBand) => void; onContinue: () => void }) {
  const ageStepTopRef = useRef<HTMLDivElement | null>(null);
  const ageContinueRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      const target = ageStepTopRef.current;
      if (!target) return;

      const top = target.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
    });
  }, []);

  const handleSelect = (value: AgeBand) => {
    onSelect(value);
    window.requestAnimationFrame(() => {
      const target = ageContinueRef.current;
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const bottomMargin = 24;
      const overflowBottom = rect.bottom - (viewportHeight - bottomMargin);

      if (overflowBottom > 0) {
        window.scrollBy({ top: overflowBottom, behavior: "smooth" });
      }
    });
  };

  return (
    <div className="mx-auto grid min-h-[calc(100svh-4rem)] w-full max-w-3xl content-center px-4 py-8 sm:px-6">
      <div ref={ageStepTopRef}>
        <Card>
        <h1 className="text-3xl font-bold text-white sm:text-4xl">Πριν ξεκινήσεις</h1>
        <p className="mt-4 max-w-2xl leading-7 text-slate-300">
          Επίλεξε το ηλικιακό σου εύρος ώστε η αναφορά επίδοσης να έχει το σωστό πλαίσιο.
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-5">
          {ageOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`rounded-lg border px-4 py-4 text-center text-lg font-bold transition ${
                selected === option.value
                  ? "border-cyan-200 bg-cyan-200/15 text-white shadow-[0_0_24px_rgba(34,211,238,0.18)]"
                  : "border-white/10 bg-white/[0.045] text-slate-200 hover:border-cyan-200/45 hover:bg-cyan-200/[0.08]"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="mt-5 rounded-lg border border-cyan-200/15 bg-cyan-200/[0.055] p-4 text-sm leading-6 text-slate-300">
          Δεν ζητάμε ακριβή ηλικία. Το ηλικιακό εύρος χρησιμοποιείται μόνο ως πληροφορία πλαισίου για την αναφορά επίδοσης.
        </p>
        <div ref={ageContinueRef}>
          <Button type="button" onClick={onContinue} disabled={!selected} className="mt-7 w-full sm:w-auto">
            Συνέχεια
          </Button>
        </div>
        </Card>
      </div>
    </div>
  );
}

export function TestPage() {
  const test = useTestAttempt();
  const [screen, setScreen] = useState<"ageRange" | "intro">("ageRange");
  const [ageBand, setAgeBand] = useState<AgeBand | null>(null);
  const introTopRef = useRef<HTMLDivElement | null>(null);
  const activeQuestionTopRef = useRef<HTMLDivElement | null>(null);
  const continueAreaRef = useRef<HTMLDivElement | null>(null);
  const lastContinueScrollRef = useRef<string | null>(null);
  const questionId = test.question?.id;

  useEffect(() => {
    if (!questionId || test.state !== "active") return;

    window.requestAnimationFrame(() => {
      const target = activeQuestionTopRef.current;
      if (!target) return;

      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY,
        behavior: "auto"
      });
    });
  }, [questionId, test.state]);

  useEffect(() => {
    if (screen !== "intro" || (test.state !== "intro" && test.state !== "loading")) return;

    window.requestAnimationFrame(() => {
      const target = introTopRef.current;
      if (!target) return;

      const top = target.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    });
  }, [screen, test.state]);

  useEffect(() => {
    lastContinueScrollRef.current = null;
  }, [questionId]);

  useEffect(() => {
    if (!questionId || test.state !== "active" || !test.selectedOption) return;

    const scrollKey = `${questionId}:${test.selectedOption}`;
    if (lastContinueScrollRef.current === scrollKey) return;

    const frame = window.requestAnimationFrame(() => {
      const target = continueAreaRef.current;
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const topMargin = 16;
      const bottomMargin = 24;
      const overflowBottom = rect.bottom - (viewportHeight - bottomMargin);
      const overflowTop = rect.top - topMargin;

      if (overflowBottom > 0) {
        window.scrollBy({ top: overflowBottom, behavior: "smooth" });
      } else if (overflowTop < 0) {
        window.scrollBy({ top: overflowTop, behavior: "smooth" });
      }

      lastContinueScrollRef.current = scrollKey;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [questionId, test.state, test.selectedOption]);

  if (screen === "ageRange") {
    return <AgeRangeStep selected={ageBand} onSelect={setAgeBand} onContinue={() => ageBand && setScreen("intro")} />;
  }

  if (test.state === "intro" || test.state === "loading") {
    return (
      <div ref={introTopRef}>
        <TestIntroScreen onStart={() => ageBand && test.begin(ageBand)} loading={test.state === "loading"} />
      </div>
    );
  }

  if (test.state === "complete") {
    return <TestCompletionScreen />;
  }

  if (test.state === "error") {
    return (
      <div className="mx-auto grid min-h-[calc(100svh-4rem)] w-full max-w-2xl content-center px-4 py-8 sm:px-6">
        <Card className="text-center">
          <AlertTriangle className="mx-auto text-cyan-200" size={34} />
          <h1 className="mt-5 text-3xl font-bold text-white">Δεν μπορέσαμε να συνεχίσουμε το test.</h1>
          <p className="mt-3 text-slate-300">Δοκίμασε ξανά σε λίγα δευτερόλεπτα.</p>
          {test.error ? <p className="mt-3 text-sm text-slate-500">{test.error}</p> : null}
          <Button type="button" onClick={() => ageBand && test.begin(ageBand)} className="mt-7 w-full sm:w-auto">
            Ξεκίνα ξανά
          </Button>
        </Card>
      </div>
    );
  }

  if (!test.question || !test.progress) return null;

  const renderContinueContent = () => (
    <div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
      <Button type="button" onClick={test.continueTest} disabled={!test.selectedOption || test.state === "submitting"} className="w-full sm:w-auto">
        {test.state === "submitting" ? "Αποθήκευση..." : "Συνέχεια"}
      </Button>
    </div>
  );

  return (
    <TestShell>
      <div ref={activeQuestionTopRef}>
        <ProgressHeader progress={test.progress} elapsedMs={test.timing.globalElapsedMs} />
      </div>
      <QuestionCard question={test.question} selectedOption={test.selectedOption} onSelect={test.selectOption} />
      <AnswerGrid question={test.question} selectedOption={test.selectedOption} onSelect={test.selectOption} />
      <div ref={continueAreaRef} className="mt-5">
        {renderContinueContent()}
      </div>
    </TestShell>
  );
}
